import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BANNER =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_82d11e62-96d8-4a58-bd86-23180e6879b5/artifacts/egnglctd_WhatsApp%20Image%202026-08-12%20at%209.45.35%20AM%20%281%29.jpeg";

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Account created!");
      }
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative z-10" data-testid="auth-page">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-primary p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_30%_30%,#EBB850,transparent_50%)]" />
        <Link
          to="/"
          className="relative z-10 flex items-center gap-2 text-white/70 hover:text-secondary transition-colors duration-200 w-fit"
          data-testid="auth-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> <span className="text-sm">Back to site</span>
        </Link>

        <div className="relative z-10">
          <motion.img
            src={BANNER}
            alt="Heal Seeal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="clip-capsule w-full max-w-md object-cover h-72 border border-white/10"
          />
          <h2 className="font-heading font-extrabold text-4xl xl:text-5xl tracking-tight text-white mt-10 leading-none">
            Seal. Protect.<br />
            <span className="font-serifaccent italic font-normal text-secondary">Heal.</span>
          </h2>
          <p className="text-white/60 mt-4 max-w-sm">
            Access your Healseeal account to manage orders and clinical resources.
          </p>
        </div>

        <p className="relative z-10 font-monotech text-xs text-white/40 tracking-widest uppercase">
          Your wound · Our care
        </p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-16 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-text-muted mb-8 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <span className="font-monotech text-xs tracking-[0.25em] uppercase text-secondary-foreground/70">
            {mode === "login" ? "Welcome back" : "Join Healseeal"}
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-primary mt-3 leading-none">
            {mode === "login" ? "Log in" : "Create account"}
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
            <AnimatePresence mode="wait">
              {mode === "register" && (
                <motion.div
                  key="name"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">Name</label>
                  <Input
                    required={mode === "register"}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="auth-name-input"
                    className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">Email</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                data-testid="auth-email-input"
                className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
              />
            </div>
            <div>
              <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">Password</label>
              <Input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                data-testid="auth-password-input"
                className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full rounded-none bg-primary text-white hover:bg-accent h-12 text-sm font-semibold tracking-wide"
            >
              {loading ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-text-muted">
            {mode === "login" ? "New to Healseeal?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-toggle-mode"
              className="font-semibold text-primary underline underline-offset-4 hover:text-secondary-foreground transition-colors"
            >
              {mode === "login" ? "Create one" : "Log in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
